import { useEffect, useState } from "react";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  Stack,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import { setAccessToken, setRefreshToken } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { minHeightDvh } from "@/utils/viewport";

type TokenState =
  | { status: "pending" }
  | { status: "valid" }
  | { status: "invalid"; reason: string };

const reasonCopy: Record<string, string> = {
  expired: "This reset link expired.",
  used: "This reset link has already been used.",
  invalid: "This reset link isn't valid.",
  rate_limited: "Too many attempts. Please try again in a minute.",
  temporary: "We couldn't validate this link right now. Please try again.",
};

function normalizeReason(reason: unknown): string {
  if (typeof reason === "string" && reason in reasonCopy) return reason;
  return "invalid";
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        ...minHeightDvh,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: (theme) =>
          theme.palette.mode === "light"
            ? "linear-gradient(160deg, #1d4ed8 0%, #6366f1 40%, #a78bfa 100%)"
            : "linear-gradient(160deg, #0c1a3d 0%, #0f0b2e 40%, #1e0a4a 100%)",
        px: 2,
      }}
    >
      <Card sx={{ width: "100%", maxWidth: 440, borderRadius: 4 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          {/* Gradient logo icon */}
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: 1,
              background: "linear-gradient(135deg, #1d4ed8, #a78bfa)",
              mx: "auto",
              mb: 1.5,
            }}
          />
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            align="center"
            fontWeight={700}
          >
            PDFGlyph
          </Typography>
          {children}
        </CardContent>
      </Card>
    </Box>
  );
}

export default function ResetPasswordPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const [tokenState, setTokenState] = useState<TokenState>({
    status: "pending",
  });
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [invalidateSessions, setInvalidateSessions] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/auth/reset-password/${token}`, {
          credentials: "omit",
        });
        if (cancelled) return;
        if (res.status === 429) {
          setTokenState({ status: "invalid", reason: "rate_limited" });
          return;
        }
        if (!res.ok) {
          setTokenState({ status: "invalid", reason: "temporary" });
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        if (data?.valid === true) {
          setTokenState({ status: "valid" });
        } else {
          setTokenState({
            status: "invalid",
            reason: normalizeReason(data?.reason),
          });
        }
      } catch {
        if (cancelled) return;
        setTokenState({ status: "invalid", reason: "invalid" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (newPassword !== confirmPassword) {
      setSubmitError("Passwords do not match");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("/auth/reset-password", {
        method: "POST",
        credentials: "omit",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword, invalidateSessions }),
      });
      const data = await res.json();
      if (res.ok) {
        setAccessToken(data.accessToken);
        setRefreshToken(data.refreshToken);
        // Populate AuthContext user. Swallow errors — the tokens are set
        // and navigation will trigger the auth flow either way.
        try {
          await refreshUser();
        } catch {
          // no-op
        }
        navigate("/dashboard");
        return;
      }
      if (data?.error === "weak_password") {
        const details = data.details;
        const msg = Array.isArray(details)
          ? details.join(" ")
          : typeof details === "string"
            ? details
            : "Password is too weak.";
        setSubmitError(msg);
      } else if (data?.error === "invalid_token") {
        setTokenState({
          status: "invalid",
          reason: normalizeReason(data?.reason),
        });
      } else {
        setSubmitError("Something went wrong. Please try again.");
      }
    } catch {
      setSubmitError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (tokenState.status === "pending") {
    return (
      <PageShell>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 160,
          }}
        >
          <CircularProgress role="status" aria-label="Loading" />
        </Box>
      </PageShell>
    );
  }

  if (tokenState.status === "invalid") {
    const copy = reasonCopy[tokenState.reason] ?? reasonCopy.invalid;
    return (
      <PageShell>
        <Alert severity="error" sx={{ mb: 2 }}>
          {copy}
        </Alert>
        <Typography variant="body2" align="center" sx={{ mt: 2 }}>
          <Link component={RouterLink} to="/forgot-password">
            Request a new one
          </Link>
        </Typography>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Typography
        variant="body2"
        color="text.secondary"
        align="center"
        sx={{ mb: 3 }}
      >
        Choose a new password
      </Typography>

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <TextField
            label="New password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            fullWidth
            autoComplete="new-password"
            autoFocus
          />
          <TextField
            label="Confirm password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            fullWidth
            autoComplete="new-password"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={invalidateSessions}
                onChange={(e) => setInvalidateSessions(e.target.checked)}
              />
            }
            label="Sign me out of other devices"
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={submitting}
            sx={{
              borderRadius: 24,
              background: "linear-gradient(135deg, #1d4ed8, #a78bfa)",
              color: "white",
              "&:hover": {
                background: "linear-gradient(135deg, #1e40af, #7c3aed)",
              },
            }}
          >
            {submitting ? "Resetting..." : "Reset password"}
          </Button>
        </Stack>
      </form>
    </PageShell>
  );
}
